import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import { AuthProvider } from "@/shared/auth/AuthContext";
import { ErrorBoundary } from "@/app/providers/ErrorBoundary";
import { NativeAppUpdate } from "@/app/native/NativeAppUpdate";
import { NativeOfflineNotice } from "@/app/native/NativeOfflineNotice";
import { NativePushBridge } from "@/app/native/NativePushBridge";
import { NativeShellGestures } from "@/app/native/NativeShellGestures";
import { PrintPreviewProvider } from "@/modules/print/PrintPreviewContext";
import { RealtimeNotifyBridge } from "@/modules/sys/realtime/RealtimeNotifyBridge";
import { clearDevServiceWorkers } from "@/platform/pwa/clearDevServiceWorkers";
import { ToastProvider, TooltipProvider } from "@/shared/ui";

const PwaLifecycle = import.meta.env.PROD
  ? lazy(() =>
    import("@/platform/pwa/PwaLifecycle").then((m) => ({
      default: m.PwaLifecycle,
    })),
  )
  : null;

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 0,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  useEffect(() => {
    void clearDevServiceWorkers();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ToastProvider>
            <AuthProvider>
              <NativePushBridge />
              <RealtimeNotifyBridge />
              <NativeOfflineNotice />
              <NativeAppUpdate />
              <NativeShellGestures />
              <PrintPreviewProvider>
                {PwaLifecycle ? (
                  <Suspense fallback={null}>
                    <PwaLifecycle />
                  </Suspense>
                ) : null}
                {children}
              </PrintPreviewProvider>
            </AuthProvider>
          </ToastProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
